import React, { useState, useEffect } from 'react';
import { Users, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('es-ES', { 
    day: '2-digit', 
    month: 'short'
  });
};

const formatDateRange = (start, end) => {
  const startDate = formatDate(start);
  const endDate = formatDate(end);
  return startDate === endDate ? startDate : `${startDate} - ${endDate}`;
};

const AbsenceItem = ({ absence, showDates = true }) => (
  <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg mb-2 last:mb-0">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-sm">
        {absence.user_name?.charAt(0).toUpperCase() || '?'}
      </div>
      <div>
        <p className="font-medium text-slate-800 text-sm">{absence.user_name}</p>
        {showDates && (
          <p className="text-xs text-slate-500">{formatDateRange(absence.start_date, absence.end_date)}</p>
        )}
      </div>
    </div>
    <Badge variant="secondary" className="text-xs">
      {absence.days_requested} {absence.days_requested === 1 ? 'día' : 'días'}
    </Badge>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="text-center py-4 text-slate-400 text-sm">
    {message}
  </div>
);

const TeamAbsencesPanel = ({ calendarData, user }) => {
  const [todayAbsences, setTodayAbsences] = useState([]);
  const [weekAbsences, setWeekAbsences] = useState([]);
  const [upcomingAbsences, setUpcomingAbsences] = useState([]);

  useEffect(() => {
    if (!calendarData || !Array.isArray(calendarData)) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const todayStr = today.toISOString().split('T')[0];
    
    // Get start and end of current week (Monday to Sunday)
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() + mondayOffset);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    
    // Next 14 days for upcoming
    const twoWeeksLater = new Date(today);
    twoWeeksLater.setDate(today.getDate() + 14);

    // Filter absences
    const todayList = [];
    const weekList = [];
    const upcomingList = [];

    calendarData.forEach(vacation => {
      // Calendar data only contains approved vacations, so no need to filter by status
      const startDate = new Date(vacation.start_date);
      const endDate = new Date(vacation.end_date);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      // Check if vacation includes today
      if (startDate <= today && endDate >= today) {
        todayList.push(vacation);
      }
      
      // Check if vacation overlaps with current week
      if (startDate <= weekEnd && endDate >= weekStart) {
        // Only add if not already in today (to avoid duplicates in display)
        if (!(startDate <= today && endDate >= today)) {
          weekList.push(vacation);
        }
      }
      
      // Check if vacation starts in next 14 days (but not this week)
      if (startDate > weekEnd && startDate <= twoWeeksLater) {
        upcomingList.push(vacation);
      }
    });

    // Sort by start date
    const sortByDate = (a, b) => new Date(a.start_date) - new Date(b.start_date);
    
    setTodayAbsences(todayList.sort(sortByDate));
    setWeekAbsences(weekList.sort(sortByDate));
    setUpcomingAbsences(upcomingList.sort(sortByDate));
  }, [calendarData]);

  const totalAbsencesToday = todayAbsences.length;

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users className="h-5 w-5 text-blue-600" />
          Ausencias del Equipo
          {totalAbsencesToday > 0 && (
            <Badge variant="destructive" className="ml-2">{totalAbsencesToday} hoy</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Today's Absences */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-red-500"></div>
            <h4 className="font-semibold text-slate-700 text-sm">Hoy</h4>
          </div>
          {todayAbsences.length > 0 ? (
            todayAbsences.map((absence, idx) => (
              <AbsenceItem key={idx} absence={absence} showDates={false} />
            ))
          ) : (
            <EmptyState message="Sin ausencias hoy" />
          )}
        </div>

        {/* This Week (excluding today) */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-amber-500"></div>
            <h4 className="font-semibold text-slate-700 text-sm">Esta semana</h4>
          </div>
          {weekAbsences.length > 0 ? (
            weekAbsences.slice(0, 3).map((absence, idx) => (
              <AbsenceItem key={idx} absence={absence} />
            ))
          ) : (
            <EmptyState message="Sin más ausencias esta semana" />
          )}
          {weekAbsences.length > 3 && (
            <p className="text-xs text-blue-600 text-center mt-1">
              +{weekAbsences.length - 3} más
            </p>
          )}
        </div>

        {/* Upcoming (next 2 weeks) */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
            <h4 className="font-semibold text-slate-700 text-sm">Próximas 2 semanas</h4>
          </div>
          {upcomingAbsences.length > 0 ? (
            upcomingAbsences.slice(0, 3).map((absence, idx) => (
              <AbsenceItem key={idx} absence={absence} />
            ))
          ) : (
            <EmptyState message="Sin ausencias programadas" />
          )}
          {upcomingAbsences.length > 3 && (
            <p className="text-xs text-blue-600 text-center mt-1">
              +{upcomingAbsences.length - 3} más
            </p>
          )}
        </div>

        {/* Warning if too many absences today */}
        {totalAbsencesToday >= 2 && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mt-3">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              <strong>Atención:</strong> Hay {totalAbsencesToday} personas ausentes hoy. 
              Considera esto antes de aprobar nuevas solicitudes.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamAbsencesPanel;
